import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Filter, ChevronDown } from 'lucide-react';
import './OrdersManagement.css';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const navigate = useNavigate();

  const statuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  const fetchOrders = useCallback(async () => {
    try {
      const url = statusFilter 
        ? `${API}/api/admin/orders?status=${statusFilter}`
        : `${API}/api/admin/orders`;
      const response = await axios.get(url, { withCredentials: true });
      setOrders(response.data.orders);
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Admin access required');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('orderStatus', newStatus);
      await axios.put(`${API}/api/admin/orders/${orderId}/status`, formData, {
        withCredentials: true
      });
      alert('Order status updated!');
      fetchOrders();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update order status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      confirmed: '#3b82f6',
      processing: '#8b5cf6',
      shipped: '#06b6d4',
      delivered: '#22c55e',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="orders-management">
      <div className="page-header">
        <h1>Orders Management</h1>
        <div className="filter-section">
          <Filter size={20} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Orders</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status.toUpperCase()}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="orders-list">
        {orders.length === 0 ? (
          <div className="empty-state">
            <Package size={64} />
            <p>No orders found</p>
          </div>
        ) : (
          orders.map(order => (
            <div key={order.id} className="order-card">
              <div className="order-header" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                <div className="order-info">
                  <h3>Order #{order.id.slice(0, 8).toUpperCase()}</h3>
                  <p>{order.email}</p>
                  <p className="order-date">{new Date(order.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="order-summary">
                  <span className="order-amount">₹{order.totalAmount || order.price}</span>
                  <span className="order-status" style={{ background: getStatusColor(order.orderStatus) }}>
                    {order.orderStatus || 'pending'}
                  </span>
                  <ChevronDown size={20} className={expandedOrder === order.id ? 'expanded' : ''} />
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="order-details">
                  <div className="detail-section">
                    <h4>Order Details</h4>
                    <p><strong>Type:</strong> {order.orderType}</p>
                    <p><strong>Payment Status:</strong> {order.paymentStatus}</p>
                    {order.productName && <p><strong>Product:</strong> {order.productName}</p>}
                    {order.items && order.items.length > 0 && (
                      <div>
                        <strong>Items:</strong>
                        <ul>
                          {order.items.map((item, idx) => (
                            <li key={idx}>{item.name} × {item.quantity}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>Delivery Address</h4>
                    {order.address && (
                      <p>
                        {order.address.street}<br />
                        {order.address.city}, {order.address.state} - {order.address.pincode}
                      </p>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>Update Status</h4>
                    <div className="status-buttons">
                      {statuses.map(status => (
                        <button
                          key={status}
                          className={`status-btn ${order.orderStatus === status ? 'active' : ''}`}
                          onClick={() => updateOrderStatus(order.id, status)}
                          style={{ borderColor: getStatusColor(status) }}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrdersManagement;